import { createFileRoute } from '@tanstack/react-router';
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  useAgentPerformance,
  useAppointmentsByMonth,
  useOffersByMonth,
  useOverview,
  useRevenue,
  usePipeline,
  usePipelineProperties,
  PIPELINE_STAGES,
  type PipelineStage,
} from '@/hooks/data/useAnalytics';
import { useAuth } from '@/hooks/useAuth';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { formatDate, formatMoney, formatNumber } from '@/lib/formatLocale';

export const Route = createFileRoute('/_app/analytics')({
  component: AnalyticsPage,
});

function AnalyticsPage() {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const isAdmin = profile?.role === 'admin';

  return (
    <section className="space-y-6">
      <header>
        <h2 className="text-2xl font-semibold tracking-tight">
          {t('analytics.title', 'Analytics')}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t('analytics.subtitle', 'Performance, Termine und Umsatz im Überblick.')}
        </p>
      </header>

      {isAdmin ? (
        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList>
            <TabsTrigger value="overview">
              {t('analytics.tabs.overview', 'Übersicht')}
            </TabsTrigger>
            <TabsTrigger value="pipeline">
              {t('analytics.tabs.pipeline', 'Pipeline')}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="overview">
            <OverviewTab />
          </TabsContent>
          <TabsContent value="pipeline">
            <PipelineTab />
          </TabsContent>
        </Tabs>
      ) : (
        <OverviewTab />
      )}
    </section>
  );
}

function OverviewTab() {
  const { t } = useTranslation();
  const { data: overview } = useOverview();
  const { data: revenue } = useRevenue();
  const { data: aptMonth = [] } = useAppointmentsByMonth();
  const { data: offerMonth = [] } = useOffersByMonth();
  const { data: agents = [] } = useAgentPerformance();

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-4">
        <Stat label={t('analytics.stats.clients', 'Klienten')} value={overview?.total_clients ?? 0} />
        <Stat label={t('analytics.stats.properties', 'Immobilien')} value={overview?.total_properties ?? 0} />
        <Stat label={t('analytics.stats.open_appointments', 'Offene Termine')} value={overview?.open_appointments ?? 0} />
        <Stat label={t('analytics.stats.active_offers', 'Aktive Anbote')} value={overview?.active_offers ?? 0} />
      </div>

      {revenue && (
        <div className="grid gap-4 md:grid-cols-3">
          <Stat
            label={t('analytics.revenue.total', 'Umsatz akzeptierter Anbote')}
            value={formatMoney((revenue.total_revenue_cents / 100), { maximumFractionDigits: 0 })}
          />
          <Stat label={t('analytics.revenue.accepted', 'Akzeptiert')} value={revenue.accepted_offers} />
          <Stat
            label={t('analytics.revenue.rejection', 'Ablehnungs-Rate')}
            value={`${(revenue.rejection_rate * 100).toFixed(1)}%`}
          />
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-2xl border border-border bg-card p-5 shadow-soft">
          <h3 className="mb-4 text-sm font-semibold">
            {t('analytics.charts.appointments', 'Termine pro Monat')}
          </h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={aptMonth}>
              <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
              <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={11} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
              <Tooltip />
              <Bar dataKey="count" fill="#1f3a52" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-2xl border border-border bg-card p-5 shadow-soft">
          <h3 className="mb-4 text-sm font-semibold">
            {t('analytics.charts.offers', 'Anbote pro Monat')}
          </h3>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={offerMonth}>
              <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
              <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={11} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
              <Tooltip />
              <Line
                type="monotone"
                dataKey="count"
                stroke="#c9a25f"
                strokeWidth={2.5}
                dot={{ r: 3, fill: '#c9a25f' }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {agents.length > 0 && (
        <div className="rounded-2xl border border-border bg-card shadow-soft">
          <h3 className="border-b border-border p-4 text-sm font-semibold">
            {t('analytics.agent_perf.title', 'Makler-Performance')}
          </h3>
          <table className="w-full text-sm">
            <thead className="bg-muted/30 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-3 text-start">Name</th>
                <th className="px-4 py-3 text-end">Termine</th>
                <th className="px-4 py-3 text-end">Anbote gesendet</th>
                <th className="px-4 py-3 text-end">Akzeptiert</th>
                <th className="px-4 py-3 text-end">Umsatz</th>
              </tr>
            </thead>
            <tbody>
              {agents.map((a) => (
                <tr key={a.agent_profile_id} className="border-t border-border/60">
                  <td className="px-4 py-3 font-medium">
                    {a.full_name}
                    <span className="ms-2 text-xs text-muted-foreground">{a.agent_id}</span>
                  </td>
                  <td className="px-4 py-3 text-end">{a.appointments_count}</td>
                  <td className="px-4 py-3 text-end">{a.offers_sent}</td>
                  <td className="px-4 py-3 text-end">{a.offers_accepted}</td>
                  <td className="px-4 py-3 text-end font-medium">
                    € {formatNumber((a.total_revenue_cents / 100), {
                      maximumFractionDigits: 0,
                    })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Pipeline tab ───────────────────────────────────────────
//
// Funnel chart of marketing_stage transitions with KPIs above and a
// drill-down dialog that lists properties currently sitting at the
// clicked stage. Date range presets default to last 90 days.

const STAGE_LABEL_KEYS: Record<PipelineStage, { key: string; fallback: string }> = {
  lead: { key: 'enums.marketing_stage.lead', fallback: 'Lead' },
  aufnahme: { key: 'enums.marketing_stage.aufnahme', fallback: 'Aufnahme' },
  active: { key: 'enums.marketing_stage.active', fallback: 'Aktiv' },
  pending: { key: 'enums.marketing_stage.pending', fallback: 'Ausstehend' },
  kaufanbot_angenommen: {
    key: 'enums.marketing_stage.kaufanbot_angenommen',
    fallback: 'Anbot angenommen',
  },
  notar_termin_geplant: {
    key: 'enums.marketing_stage.notar_termin_geplant',
    fallback: 'Notar-Termin geplant',
  },
  notariell_verkauft: {
    key: 'enums.marketing_stage.notariell_verkauft',
    fallback: 'Notariell verkauft',
  },
};

const STAGE_COLORS: Record<PipelineStage, string> = {
  lead: '#94a3b8',
  aufnahme: '#fb923c',
  active: '#c9a25f',
  pending: '#a78bfa',
  kaufanbot_angenommen: '#60a5fa',
  notar_termin_geplant: '#34d399',
  notariell_verkauft: '#10b981',
};

const RANGE_PRESETS = [
  { key: '30', days: 30, label: '30T' },
  { key: '90', days: 90, label: '90T' },
  { key: '180', days: 180, label: '6M' },
  { key: '365', days: 365, label: '12M' },
];

function PipelineTab() {
  const { t, i18n } = useTranslation();
  const [presetDays, setPresetDays] = useState<number>(90);
  const [drillStage, setDrillStage] = useState<PipelineStage | null>(null);

  const range = useMemo(() => {
    const now = Date.now();
    return { from: now - presetDays * 86_400_000, to: now };
  }, [presetDays]);

  const { data: pipeline, isLoading } = usePipeline(range);
  const { data: properties = [] } = usePipelineProperties(drillStage);

  const chartData = useMemo(() => {
    if (!pipeline) return [];
    return pipeline.stages.map((s) => ({
      stage: s.stage,
      label: t(STAGE_LABEL_KEYS[s.stage].key, STAGE_LABEL_KEYS[s.stage].fallback),
      entered: s.entered_count,
      current: s.current_count,
      conversion: Math.round(s.conversion_rate * 100),
    }));
  }, [pipeline, t]);


  return (
    <div className="space-y-5">
      {/* Range presets */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-xs text-ink-3">
          <span className="uppercase tracking-[0.08em]">
            {t('analytics.pipeline.range', 'Zeitraum')}
          </span>
          {RANGE_PRESETS.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => setPresetDays(p.days)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                presetDays === p.days
                  ? 'bg-ink text-paper'
                  : 'border border-line bg-card text-ink-2 hover:bg-paper-2'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        {pipeline && (
          <span className="text-xs text-ink-3">
            {formatDate(pipeline.range.from)} —{' '}
            {formatDate(pipeline.range.to)}
          </span>
        )}
      </div>

      {/* KPIs */}
      <div className="grid gap-4 md:grid-cols-4">
        <Stat
          label={t('analytics.pipeline.kpi_revenue', 'Umsatz (geschlossen)')}
          value={
            pipeline
              ? formatMoney(pipeline.total_revenue_cents / 100)
              : '—'
          }
        />
        <Stat
          label={t('analytics.pipeline.kpi_closed', 'Abschlüsse')}
          value={pipeline?.closed_count ?? '—'}
        />
        <Stat
          label={t('analytics.pipeline.kpi_avg_days', 'Ø Tage Lead → Notar')}
          value={pipeline?.avg_lead_to_close_days ?? '—'}
        />
        <Stat
          label={t('analytics.pipeline.kpi_dropoff', 'Drop-off Rate')}
          value={
            pipeline
              ? `${(pipeline.drop_off_overall * 100).toFixed(0)}%`
              : '—'
          }
        />
      </div>

      {/* Funnel chart */}
      <div className="rounded-2xl border border-border bg-card p-5 shadow-soft">
        <h3 className="mb-1 text-sm font-semibold">
          {t('analytics.pipeline.funnel_title', 'Konversions-Trichter')}
        </h3>
        <p className="mb-4 text-xs text-ink-3">
          {t(
            'analytics.pipeline.funnel_hint',
            'Klick auf eine Stufe öffnet die Liste der Immobilien, die aktuell dort sitzen.',
          )}
        </p>
        {isLoading ? (
          <p className="py-12 text-center text-sm text-ink-3">
            {t('common.loading', 'Lädt …')}
          </p>
        ) : chartData.length === 0 ? (
          <p className="py-12 text-center text-sm text-ink-3">
            {t('analytics.pipeline.empty', 'Keine Stage-Wechsel im Zeitraum.')}
          </p>
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ top: 8, right: 24, bottom: 8, left: 24 }}
            >
              <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
              <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={11} />
              <YAxis
                type="category"
                dataKey="label"
                stroke="hsl(var(--muted-foreground))"
                fontSize={11}
                width={140}
              />
              <Tooltip
                formatter={(value: number, key: string) => {
                  if (key === 'entered') return [value, t('analytics.pipeline.entered', 'Eingestiegen')];
                  if (key === 'current') return [value, t('analytics.pipeline.current', 'Aktuell hier')];
                  return [value, key];
                }}
                cursor={{ fill: 'hsl(var(--paper-2))' }}
              />
              <Bar
                dataKey="entered"
                radius={[0, 6, 6, 0]}
                onClick={(d) => setDrillStage(d.stage as PipelineStage)}
                cursor="pointer"
              >
                {chartData.map((d) => (
                  <Cell key={d.stage} fill={STAGE_COLORS[d.stage as PipelineStage]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Per-stage breakdown table */}
      {pipeline && (
        <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-soft">
          <table className="w-full text-sm">
            <thead className="bg-muted/30 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-3 text-start">{t('analytics.pipeline.col_stage', 'Phase')}</th>
                <th className="px-4 py-3 text-end">{t('analytics.pipeline.entered', 'Eingestiegen')}</th>
                <th className="px-4 py-3 text-end">{t('analytics.pipeline.exited', 'Ausgestiegen')}</th>
                <th className="px-4 py-3 text-end">{t('analytics.pipeline.current', 'Aktuell hier')}</th>
                <th className="px-4 py-3 text-end">{t('analytics.pipeline.conversion', 'Konversion')}</th>
              </tr>
            </thead>
            <tbody>
              {pipeline.stages.map((s, i) => (
                <tr
                  key={s.stage}
                  className="cursor-pointer border-t border-border/60 transition-colors hover:bg-paper-2"
                  onClick={() => setDrillStage(s.stage)}
                >
                  <td className="px-4 py-3 font-medium">
                    <span
                      className="me-2 inline-block h-2 w-2 rounded-full align-middle"
                      style={{ background: STAGE_COLORS[s.stage] }}
                    />
                    {t(STAGE_LABEL_KEYS[s.stage].key, STAGE_LABEL_KEYS[s.stage].fallback)}
                  </td>
                  <td className="px-4 py-3 text-end">{s.entered_count}</td>
                  <td className="px-4 py-3 text-end">{s.exited_count}</td>
                  <td className="px-4 py-3 text-end font-semibold">{s.current_count}</td>
                  <td className="px-4 py-3 text-end text-ink-2">
                    {i === 0 ? '—' : `${Math.round(s.conversion_rate * 100)}%`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Drill-down dialog */}
      <Dialog open={drillStage != null} onOpenChange={(o) => !o && setDrillStage(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              {drillStage
                ? t(STAGE_LABEL_KEYS[drillStage].key, STAGE_LABEL_KEYS[drillStage].fallback)
                : ''}{' '}
              <span className="text-sm font-normal text-ink-3">
                · {properties.length}{' '}
                {t('analytics.pipeline.properties_unit', 'Immobilien')}
              </span>
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            {properties.length === 0 ? (
              <p className="py-8 text-center text-sm text-ink-3">
                {t('analytics.pipeline.no_properties', 'Keine Immobilien in dieser Phase.')}
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-card text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-start">{t('analytics.pipeline.col_address', 'Adresse')}</th>
                    <th className="px-3 py-2 text-end">{t('analytics.pipeline.col_price', 'Preis')}</th>
                    <th className="px-3 py-2 text-end">m²</th>
                    <th className="px-3 py-2 text-end">{t('analytics.pipeline.col_days', 'Tage')}</th>
                  </tr>
                </thead>
                <tbody>
                  {properties.map((p) => (
                    <tr key={p.id} className="border-t border-border/60">
                      <td className="px-3 py-2">
                        <p className="font-medium">{p.address ?? '—'}</p>
                        <p className="text-xs text-ink-3">
                          {[p.district, p.city].filter(Boolean).join(' · ')}
                        </p>
                      </td>
                      <td className="px-3 py-2 text-end">
                        {p.price
                          ? formatMoney(p.price)
                          : '—'}
                      </td>
                      <td className="px-3 py-2 text-end">{p.m2 ?? '—'}</td>
                      <td className="px-3 py-2 text-end">
                        {p.days_in_stage != null ? p.days_in_stage : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-2xl border border-border bg-card/60 p-5 shadow-soft">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-2 text-2xl font-semibold tracking-tight">{value}</p>
    </div>
  );
}
